import * as api from '../utils/api';

/**
 * FIXERLIGHT-052 — cross-repo contract with
 * `docs/specs/fixerlight-052-mes-rendez-vous-atelier.md` (repo api).
 * Lot 1 (api#875) : lecture, `GET` seulement. Lot 2 (api#882/#892) : annuler
 * et déplacer, `POST`. Same auth as `/clients/{client}/courses`
 * (`ClientAccess` + `checkObjectAccess()` server-side) — same `api.get`/
 * `api.post`, same headers, same token handling as the rest of
 * `src/utils/api.jsx`.
 *
 * The api already returns the list pre-sorted (upcoming ascending first,
 * then past most-recent-first, § 2 of the contract) — the front must NEVER
 * re-sort it.
 */
export const WORKSHOP_BOOKINGS_PATH = (clientId) =>
  `/clients/${clientId}/workshop-bookings`;
export const WORKSHOP_BOOKING_PATH = (clientId, bookingId) =>
  `/clients/${clientId}/workshop-bookings/${bookingId}`;
export const WORKSHOP_BOOKING_CANCEL_PATH = (clientId, bookingId) =>
  `${WORKSHOP_BOOKING_PATH(clientId, bookingId)}/cancel`;
export const WORKSHOP_BOOKING_RESCHEDULE_PATH = (clientId, bookingId) =>
  `${WORKSHOP_BOOKING_PATH(clientId, bookingId)}/reschedule`;

/**
 * `api.post`/`api.get` (`src/utils/api.jsx`) never reject on a 4xx/5xx —
 * they resolve the parsed body regardless of status (`xhr.onload` always
 * resolves). The api's error shape on these `/v1` routes is
 * `{ error_message, errors, code? }` (`app/Exceptions/Handler.php`) — `code`
 * is the machine discriminant (`booking_too_close`, `slot_not_available`,
 * `booking_not_cancellable`, `booking_not_confirmed`…), present only on a
 * business refusal. This class carries it so callers can map it to a
 * precise message instead of a generic failure banner.
 */
export class WorkshopBookingActionError extends Error {
  constructor(code, message) {
    super(message || code || 'workshop-booking-action-error');
    this.name = 'WorkshopBookingActionError';
    this.code = code || null;
  }
}

/**
 * @param {number|string} clientId
 * @returns {Promise<Array>} the bookings, in the exact order the api sent
 * them.
 * @throws when the transport fails or the response isn't the expected
 * `{ data: [...] }` shape — callers must catch this and show an explicit
 * error, never fail silently.
 */
export async function getWorkshopBookings(clientId) {
  const response = await api.get(WORKSHOP_BOOKINGS_PATH(clientId));
  if (!response || !Array.isArray(response.data)) {
    throw new Error('workshop-bookings: unexpected response');
  }
  return response.data;
}

/**
 * @param {number|string} clientId
 * @param {number|string} bookingId
 * @returns {Promise<Object>} the booking.
 * @throws on transport failure, a 404 (booking not found / not this
 * client's), or any other unexpected shape.
 */
export async function getWorkshopBooking(clientId, bookingId) {
  const response = await api.get(WORKSHOP_BOOKING_PATH(clientId, bookingId));
  if (!response || !response.data) {
    throw new Error('workshop-bookings: unexpected response');
  }
  return response.data;
}

/**
 * @param {number|string} clientId
 * @param {number|string} bookingId
 * @returns {Promise<Object>} the booking, updated (`status: 'cancelled'`).
 * @throws {WorkshopBookingActionError} on a business refusal (`.code` set)
 * or an unreadable response (`.code` null) — always show an explicit error,
 * never fail silently.
 */
export async function cancelWorkshopBooking(clientId, bookingId) {
  const response = await api.post(
    WORKSHOP_BOOKING_CANCEL_PATH(clientId, bookingId),
  );
  if (response && response.code) {
    throw new WorkshopBookingActionError(response.code, response.error_message);
  }
  if (!response || !response.data) {
    throw new WorkshopBookingActionError(
      null,
      'workshop-bookings: unexpected response',
    );
  }
  return response.data;
}

/**
 * @param {number|string} clientId
 * @param {number|string} bookingId
 * @param {string|string[]} slot ISO 8601. A string is ONE new time
 * (`scheduled_at`, instant account: the booking moves at once). An array is
 * the 1-10 one-hour slots proposed to a confirm-required account
 * (`proposed_slots`, FIX-1155): the booking keeps its time until the
 * workshop picks one; an instant account refuses it in 422
 * `single_slot_required`.
 * @returns {Promise<Object>} the booking, updated (new `scheduled_at` on an
 * instant account, a `reschedule_request` carrying every proposed slot on a
 * confirm-required one).
 * @throws {WorkshopBookingActionError} same contract as
 * {@link cancelWorkshopBooking}.
 */
export async function rescheduleWorkshopBooking(clientId, bookingId, slot) {
  const response = await api.post(
    WORKSHOP_BOOKING_RESCHEDULE_PATH(clientId, bookingId),
    Array.isArray(slot) ? { proposed_slots: slot } : { scheduled_at: slot },
  );
  if (response && response.code) {
    throw new WorkshopBookingActionError(response.code, response.error_message);
  }
  if (!response || !response.data) {
    throw new WorkshopBookingActionError(
      null,
      'workshop-bookings: unexpected response',
    );
  }
  return response.data;
}
